import TrackingEvent from 'constants/trackingEvents';
import { Space } from 'interfaces/space';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { goToSpaces } from 'utils/routerHelpers';
import { isAdmin } from 'utils/userHelpers';

import React from 'react';

import { Badge } from '@thinkific/chalk/Badge';
import { TabNav } from '@thinkific/chalk/TabNav';
import { atomic } from '@thinkific/chalk/atomic';
import { Eye, LockSimple } from '@thinkific/chalk/react-icons';

import { useCommunityContext } from 'components/CommunityContext';
import CommunityUsersAvatars from 'components/CommunityUsers/components/CommunityUsersAvatars';

import { SpaceInfoTabs } from './SpaceInfo.types';

interface SpaceInfoProps {
  space: Space;
}

export const spaceInfoContainer = atomic({
  backgroundColor: 'onLight.surface.primary',
  color: 'onLight.text.primary',
  borderRadius: 'sm',
  fontSize: 'display.md',
  boxShadow: 'onLight.shadow.sm',
  paddingBottom: 'space.600',
  position: 'relative',
  marginBottom: 'space.400',
});

export const centeredContainer = atomic({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  gap: 'space.100',
});

export const spaceDetailsContainer = atomic({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'space.100',
  justifyContent: 'space-between',
  padding: 'space.200',
  alignItems: 'center',
});

export const spaceNameContainer = atomic({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'space.100',
});

export const tabContainer = atomic({
  position: 'absolute',
  bottom: 0,
  width: 'full',
});

export const truncateName = atomic({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const SpaceInfo = ({ space }: SpaceInfoProps) => {
  const { t } = useTranslation();
  const { community, currentUser, trackEvent, NLACommunitiesEnabled } = useCommunityContext();
  const router = useRouter();
  const { spaceId } = router.query;
  const isPrivate = space.isExclusive;

  const tabs = [
    ...(!NLACommunitiesEnabled
      ? [
          {
            id: SpaceInfoTabs.INDEX,
            label: t('components-spaceInfo-posts-tab', 'Feed'),
            href: `/${community.id}/spaces/${spaceId}`,
            dataQa: 'space-info__feed-tab',
            onClick: (e) => {
              e.preventDefault();
              trackEvent(TrackingEvent.GO_TO_SPACE_FEED);
              goToSpaces(router, spaceId as string, community.id, true);
            },
          },
        ]
      : []),
    {
      id: SpaceInfoTabs.MEMBERS,
      label: t('components-spaceInfo-members-tab', 'Members'),
      href: `/${community.id}/spaces/${spaceId}/members`,
      dataQa: 'space-info__members-tab',
      onClick: (e) => {
        e.preventDefault();
        trackEvent(TrackingEvent.GO_TO_SPACE_MEMBERS);
        goToSpaces(router, spaceId as string, community.id, true, 'members');
      },
    },
    ...(isAdmin(currentUser)
      ? [
          {
            id: SpaceInfoTabs.EDIT,
            label: t('components-spaceInfo-edit-tab', 'Settings'),
            href: `/${community.id}/spaces/${spaceId}/settings`,
            dataQa: 'space-info__settings-tab',
            onClick: (e) => {
              e.preventDefault();
              trackEvent(TrackingEvent.GO_TO_SPACE_SETTINGS);
              goToSpaces(router, spaceId as string, community.id, true, 'edit');
            },
          },
        ]
      : []),
  ];

  const totalUsersCount = space.spaceMembers?.totalCount || 0;

  return (
    <div className={spaceInfoContainer}>
      <div className={spaceDetailsContainer}>
        <span className={spaceNameContainer}>
          <span className={truncateName}>{space.name}</span>
          {isPrivate && <LockSimple className={atomic({ paddingLeft: 'space.050' })} size="xs" />}
          {space.readOnly && (
            <Badge
              icon={Eye}
              label={t('components-spaceInfo-read_only_space', 'Read only')}
              variant="ghost"
            />
          )}
        </span>
        <CommunityUsersAvatars
          tooltipTitle={t(
            'components-spaceInfo-total_member_count_title',
            '{{spaceName}} has {{userCount}} members',
            { spaceName: space.name, userCount: totalUsersCount }
          )}
          totalUsers={totalUsersCount}
          users={space.spaceMembers?.edges?.map(({ node }) => node.user)?.splice(0, 5)}
        />
      </div>
      <div className={tabContainer}>
        <TabNav
          currentTabId={tabs.find((tab) => router.route.includes(tab.id))?.id || 'index'}
          tabs={tabs}
        />
      </div>
    </div>
  );
};

export default SpaceInfo;
